<template>
    <div id="modal-export-lut" class="modal-new hidden">
        <svg class="btn-close-modal" xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            <path d="M0 0h24v24H0z" fill="none" />
        </svg>
        <span class="title">Download a LUT</span>

        <label for="name" theme="--start">Name</label>
        <div name="export-lut-name-container">
            <input
                    type="text"
                    v-model="name"
                    name="export-lut-name"
                    placeholder="My Exported LUT #5"
            />
        </div>

        <div class="buttons-continer">
            <button type="button" class="modal-btn --primary" @click="onSubmit" :disabled="exporting">
                <v-progress-circular v-if="exporting" indeterminate color="var(--primary)" size="18"
                                     width="3"></v-progress-circular>
                <span v-else>Export</span>
            </button>
        </div>
    </div>
</template>

<script>
import JSZip from 'jszip';

import { required } from 'vuelidate/lib/validators';
import { mapState } from 'vuex';
import download from 'downloadjs';

export default {
    data() {
        return {
            name: '',
            exporting: false,
        };
    },
    computed: {
        ...mapState('Projects', ['action']),
    },
    watch: {
        action(newValue) {
            if (this.action.action == 'export-lut-from-asset' && this.action.data.length == 1) {
                // because on every modal show, we clear all input fields
                setTimeout(() => {
                    this.name = newValue.data[0].nicename;
                }, 200);
            }
        },
    },
    methods: {
        async onSubmit() {

            this.exporting = true;

            let data = {
                name: this.name || `Lutify.me LUT ${Date.now()}`,
            };

            if (this.action.action == 'export-lut-from-asset') {
                await window.lutify.storeLastState();

                let exportLutFromAssets = [];
                let selectedAssets = this.action.data;
                window.lutify.exportingLut = true; // to not switch between tabs on right

                for await (const asset of selectedAssets) {
                    await window.lutify.applyCompare(asset.blocks, 'waitToLoad', false);
                    let name = selectedAssets.length == 1 ? data.name : asset.nicename;
                    let lutFiles = await window.luts.prepareDownloadLuts({ ...data, name });

                    exportLutFromAssets.push({
                        folderName: asset.nicename,
                        luts: lutFiles,
                    });
                }

                this.downloadLuts(exportLutFromAssets);
                await window.lutify.applyLastState(this.$store.state.Projects.image);

                window.lutify.exportingLut = false;

                await this.$store.dispatch('Projects/setAction', {
                    type: null,
                    action: 'export-lut-from-asset-complete',
                    data: null,
                });
            }

            // export for current image
            else {
                let lutFiles = await window.luts.prepareDownloadLuts(data);
                this.downloadLuts([
                    {
                        luts: lutFiles,
                    },
                ]);
            }

            this.exporting = false;
            window.UI.hideModal();
            this.name = '';
        },

        downloadLuts(data) {
            let zip = new JSZip();

            // 1 "folder" / 1 lut
            if (data.length == 1 && data[0].luts.length == 1) {
                download(data[0].luts[0], data[0].luts[0].name);
            } else {
                let folderNamesList = [];
                let folderPath;

                data.forEach(folder => {
                    if (folderNamesList.includes(folder.folderName)) {
                        for (let i = 1; i < 100; i++) {
                            if (!folderNamesList.includes(`${folder.folderName}-${i}`)) {
                                folderPath = `${folder.folderName}-${i}/`;
                                folderNamesList.push(`${folder.folderName}-${i}`);
                                break;
                            }
                        }
                    } else {
                        folderPath = `${folder.folderName}/`;
                        folderNamesList.push(folder.folderName);
                    }

                    folder.luts.forEach(lut => {
                        let path;

                        // when we export from multiple assets
                        if (data.length > 1) path = folderPath + lut.name;
                        // when exporting from 1 asset, but several luts
                        else path = lut.name;

                        zip.file(path, lut);
                    });
                });
                zip.generateAsync({ type: 'blob' }).then(blob => download(blob, `LUT-export-${Date.now()}.zip`));
            }
        },
    },
};
</script>

<style lang="scss">
#modal-export-lut {
	.switch-container {
		margin-left: initial;
	}

	.option-container {
		&.--disabled {
			opacity: 0.25;

			input {
				pointer-events: none;
				cursor: not-allowed;
			}
		}

		.option-title {
			line-height: 20px;
		}
	}

	.btn-advanced-options {
		margin-bottom: 0;

		> span {
			line-height: 20px;
		}
	}

	label {
		margin-bottom: 10px !important;
	}

	textarea,
	input {
		line-height: 20px;
	}

	textarea {
		width: 100%;
		margin-bottom: 12px;
	}

	.description-container {
		margin-bottom: 8px;

		.v-expansion-panel-header {
			span {
				line-height: 12px;
			}
		}
	}

	div[name="export-lut-name-container"] {
		margin-bottom: 20px;

		input {
			background: white;
			border-radius: 5px;
			border: none;
			color: #121212;
			padding: 7px 10px;
			font-size: 1rem;
			width: 100%;

			&:disabled {
				opacity: 0.3;
				cursor: not-allowed;
			}
		}
	}

	.buttons-continer {
		margin-top: 10px;
	}

	.v-expansion-panel {
		background: transparent;
		color: inherit;

		&:before {
			display: none;
		}

		.v-expansion-panel-header {
			padding: 0;
			min-height: auto;
			flex-direction: row-reverse;
			margin-bottom: 10px;

			&:before {
				display: none;
			}

			.v-expansion-panel-header__icon i {
				font-size: 18px;
				color: var(--font-default-color);
			}
		}

		.v-expansion-panel-content__wrap {
			padding: 0;
		}
	}
}
</style>
